import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import ChatPreview from '../chat-preview'
import { pluralize } from '../format'
import useStyles from '../styles'
import SectionTitle from 'components/section-title'

export default function ReviewStep({
  campaign,
  audienceCount,
  delivery,
  scheduledFor,
  minScheduledFor,
  maxScheduledFor,
  scheduleError,
  onDeliveryChange,
  onScheduledForChange,
}) {
  const classes = useStyles()

  return (
    <>
      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>{campaign.name}</SectionTitle>
          <Typography className={classes.cardSubtitle}>
            Goes to {pluralize(audienceCount, 'student')}
            {campaign.offerings.length > 0
              ? ` · ${pluralize(campaign.offerings.length, 'offering')}`
              : ''}
            {campaign.media.length > 0
              ? ` · ${pluralize(campaign.media.length, 'attachment')}`
              : ''}
          </Typography>
        </Box>
        <Box className={classes.cardBody}>
          <Typography className={classes.fieldGroupLabel}>
            What students see
          </Typography>
          <ChatPreview
            body={campaign.body}
            offerings={campaign.offerings}
            media={campaign.media}
          />
          {campaign.offerings.length > 0 ? (
            <Typography className={classes.helperText}>
              Bookings made from these cards count as “Booked from link.”
              Bookings from recipients within 14 days that didn’t use a card
              count separately as “Booked later.”
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box className={classes.card}>
        <Box className={classes.cardHeader}>
          <SectionTitle>When to send</SectionTitle>
        </Box>
        <Box className={classes.cardBody}>
          <RadioGroup
            value={delivery}
            onChange={event => onDeliveryChange(event.target.value)}
            aria-label="When to send"
          >
            <FormControlLabel
              value="now"
              control={<Radio color="primary" />}
              label="Send now"
            />
            <FormControlLabel
              value="later"
              control={<Radio color="primary" />}
              label="Schedule for later"
            />
          </RadioGroup>
          {delivery === 'later' ? (
            <TextField
              id="campaign-send-on"
              type="datetime-local"
              variant="outlined"
              label="Send on"
              value={scheduledFor}
              onChange={event => onScheduledForChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: minScheduledFor, max: maxScheduledFor }}
              error={Boolean(scheduleError)}
              helperText={scheduleError || 'Uses your computer’s time zone.'}
              style={{ maxWidth: 320 }}
            />
          ) : null}
        </Box>
      </Box>
    </>
  )
}

ReviewStep.propTypes = {
  campaign: PropTypes.shape({
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    offerings: PropTypes.array.isRequired,
    media: PropTypes.array.isRequired,
  }).isRequired,
  audienceCount: PropTypes.number.isRequired,
  delivery: PropTypes.oneOf(['now', 'later']).isRequired,
  scheduledFor: PropTypes.string.isRequired,
  minScheduledFor: PropTypes.string.isRequired,
  maxScheduledFor: PropTypes.string.isRequired,
  scheduleError: PropTypes.string,
  onDeliveryChange: PropTypes.func.isRequired,
  onScheduledForChange: PropTypes.func.isRequired,
}
